import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';

import { useMovie } from "../movie/MovieProvider";
import { useMovieSetter } from "../movie/MovieProvider";

import VEElement from './VEElement';
import VEField from './fields/VEField';
import VEFieldsConfig from './fields/VEFieldsConfig';
import VECSSPropertyAdder from './fields/VECSSPropertyAdder';

import ControlPointDuplicateIcon from '@mui/icons-material/ControlPointDuplicate';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import MovieIcon from '@mui/icons-material/Movie';
import VideoLabelIcon from '@mui/icons-material/VideoLabel';

import { Tabs, Tab } from '@mui/material';

export default function VESettingsPanel(props) {

    const movie = useMovie();
    const setMovie = useMovieSetter();

    const cssProperties = props.componentsConfig?.cssProperties;

    // refresh form
    const [refreshForm, setRefreshForm] = React.useState(0);

    // panelWidth defines the width of the settings panel
    const panelWidth = "300px";

    // elementSchema describes the schema of the selected element
    const [elementSchema, setElementSchema] = React.useState(null);

    let componentSettingsSchema = null;

    // formFields describes the final fields that will be shown in the settings panel
    const [formFields, setFormFields] = React.useState({});

    const [selectedTab, setSelectedTab] = React.useState("form");

    // info describes the title and icon of the selected element
    let info = {};

    // fieldGroups describes the fields that will be shown in the settings panel
    const fieldGroups = VEFieldsConfig(movie.selected?.type);

    const componentsBaseURL = "https://tpl.json2video.com/data/components";

    // Helper constants
    const componentsSchema = props.componentsSchema;
    const selectedItem = movie.selected;
    const selectedElement = selectedItem?.pointer;
    const isMovie = (movie.selected?.type === "movie") ? true : false;
    const elementType = (movie.selected?.type === "element") ? selectedElement?.type : movie.selected?.type;

    React.useEffect(() => {
        console.log("Panel: ", selectedItem);
        async function myAsyncFunction() {
            prepareFormFields(await prepareElementSchema());
        }
        myAsyncFunction();
    }, [selectedItem?.itemId, refreshForm]);

    // If there is no selected item, return empty panel
    if (!selectedItem) return (
        <Box key="settings-panel" className="m-1 border border-gray-200 text-xs overflow-hidden flex flex-col" style={{ width: panelWidth }}>
            No item selected
        </Box>
    );

    return (
        <Box key="settings-panel" className="m-1 border border-gray-200 text-xs overflow-hidden flex flex-col" style={{ width: panelWidth }}>
            {/* PANEL HEADER */}
            <Stack className="h-8 pl-2 bg-yellow-200 flex items-center justify-between sticky" direction="row">
                <Box className="w-9/12 flex items-center text-md font-bold">
                    {info.icon}
                    <span className="ml-1 truncate">{
                        // Capitalize elementType first letter
                        elementType.charAt(0).toUpperCase() + elementType.slice(1)
                    }</span>
                </Box>
                <Box className="flex flex-nowrap items-center">
                    {
                        !isMovie &&
                        <>
                            <IconButton onClick={duplicateItem}><ControlPointDuplicateIcon /></IconButton>
                            <IconButton onClick={deleteItem}><DeleteForeverIcon /></IconButton>
                        </>
                    }
                </Box>
            </Stack>
            <Tabs value={selectedTab} onChange={(tabId, selectedTab) => setSelectedTab(selectedTab)}>
                <Tab value="form" label="Form" />
                <Tab value="schema" label="Schema" />
                <Tab value="fields" label="Fields" />
            </Tabs>
            <Box key="settings-panel-field-list" className="h-0 flex-grow overflow-y-scroll">
                {
                    (selectedTab === "form") && (
                        showForm()
                    )
                }
                {
                    (selectedTab === "schema") && (
                        (typeof elementSchema === "object") ?
                            <pre>{JSON.stringify(elementSchema, null, 2)}</pre> : elementSchema
                    )
                }
                {
                    (selectedTab === "fields") && (
                        (typeof formFields === "object") ?
                            <pre>{JSON.stringify(formFields, null, 2)}</pre> : formFields
                    )
                }
            </Box>
        </Box>
    );

    async function prepareElementSchema() {

        console.log("cssProperties: ", cssProperties);

        // Prepare element schema
        let schema = {};
        console.log("prepareElementSchema(): Clear the schema");

        if (!selectedItem) {
        }
        else if (selectedItem.type === "movie") {
            info.title = "Movie" + (selectedItem.pointer.comment ? ": " + selectedItem.pointer.comment : "");
            info.icon = <MovieIcon />;
            schema = JSON.parse(JSON.stringify(props.schema.components.schemas["movie"])) ?? {};
        }
        else if (selectedItem.type === "scene") {
            // console.log("selectedItem: ", selectedItem);
            info.title = "Scene #" + (selectedItem.sceneIndex + 1) + (selectedItem.pointer.comment ? ": " + selectedItem.pointer.comment : "");
            info.icon = <VideoLabelIcon />;
            schema = JSON.parse(JSON.stringify(props.schema.components.schemas["scene"])) ?? {};
        }
        else if (selectedItem.type === "element" && selectedItem.pointer) {
            schema = JSON.parse(JSON.stringify(props.schema.components.schemas[selectedItem.pointer.type])) ?? {};

            if (["component", "text"].includes(elementType)) {
                // attach component settings
                schema.properties.settings = schema.properties.settings ?? {};

                // get component package and component id
                // depending on the selected element type
                let componentPackage, componentId;
                if (elementType === "component" && "component" in selectedItem.pointer) {
                    [componentPackage, componentId] = selectedElement.component.split("/");
                }
                else if (elementType === "text") {
                    componentPackage = "text";
                    componentId = "_default";
                }
                else {
                    console.log("Could not set the componentPackage and componentId: ", elementType, selectedItem.pointer);
                }

                // if componentPackage and componentId exist
                // add the settings properties into the schema
                if (componentPackage && componentId) {
                    
                    // load current component settings schema from: /data/components/schemas/<componentPackage>/<componentId>.json
                    componentSettingsSchema =
                        await fetch(`${componentsBaseURL}/schemas/${componentPackage}/${componentId}.json`)
                            .then(r => r.json())
                            .catch(e => {
                                console.log("Could not load component settings schema: ", componentPackage, componentId);
                                return null;
                            });

                    if (componentSettingsSchema) {
                        // copy settings defaults from the component package into the schema
                        schema.properties.settings = {
                            ...schema.properties.settings,
                            clonable: componentSettingsSchema.clonable ?? null,
                            maxClones: componentSettingsSchema.maxClones ?? 0,
                            properties: { ...componentSettingsSchema.settings }
                        };

                        // add other settings in the selectedElement if not present in the settings schema
                        // example: properties added with the (+) button
                        if (elementType === "component" && "settings" in selectedElement) {
                            // for component elements,
                            // go through all items in the component settings
                            Object.keys(selectedElement.settings).forEach(item => {
                                schema.properties.settings.properties[item] = schema.properties.settings.properties[item] || {};
                                schema.properties.settings.properties[item].properties = schema.properties.settings.properties[item].properties || {};

                                // add the title of the item
                                if (item.startsWith(componentSettingsSchema.clonable)) {
                                    const itemNumber = item.slice(componentSettingsSchema.clonable.length);
                                    schema.properties.settings.properties[item].title = 
                                        componentSettingsSchema.clonable.at(0).toUpperCase() + componentSettingsSchema.clonable.slice(1) + " " + itemNumber;
                                }

                                // go through all properties in the item
                                Object.keys(selectedElement.settings[item]).forEach(property => {
                                    const propertyCSS = cssProperties[property] ?? {};

                                    console.log("property: ", property, "css: ", propertyCSS);

                                    // if the property is animate, add the existing properties inside the animate object
                                    if (property === "animate") {
                                        // create the animate object if it does not exist in the schema
                                        if (typeof schema.properties.settings.properties[item].properties.animate !== "object" || schema.properties.settings.properties[item].properties.animate === null) {
                                            schema.properties.settings.properties[item].properties.animate = {};
                                        }
                                        schema.properties.settings.properties[item].properties.animate.properties = schema.properties.settings.properties[item].properties.animate.properties || {};

                                        // add any properties in the selectedElement animate object and add them to the schema
                                        Object.keys(selectedElement.settings[item].animate).forEach(animateProperty => {
                                            const propertyCSS = cssProperties[animateProperty] ?? {};
                                            console.log("animateProperty: ", animateProperty, "css: ", propertyCSS);

                                            if (!(animateProperty in schema.properties.settings.properties[item].properties.animate.properties)) {
                                                schema.properties.settings.properties[item].properties.animate = schema.properties.settings.properties[item].properties.animate || {};
                                                schema.properties.settings.properties[item].properties.animate.properties = schema.properties.settings.properties[item].properties.animate.properties || {};
                                                schema.properties.settings.properties[item].properties.animate.properties[animateProperty] = applySchemaDefaults(animateProperty,{}, propertyCSS, true);
                                            }
                                        });
                                    }

                                    // if the property is not defined in the schema
                                    // add the property to the schema with the defaults in "propertyCSS"
                                    if (!(property in schema.properties.settings.properties[item].properties)) {
                                        schema.properties.settings.properties[item].properties[property] = {};
                                    }

                                    // add the propertyCSS data to the schema
                                    schema.properties.settings.properties[item].properties[property] = applySchemaDefaults(property, schema.properties.settings.properties[item].properties[property], propertyCSS, true);
                                });
                            });

                            // reorder the items for clonable items
                            // markLastClonableItem(schema.properties.settings.properties, componentSettingsSchema);
                        }
                        else if (elementType === "text" && "settings" in selectedElement) {
                            // for text elements
                            // go through the properties in the settings
                            Object.keys(selectedElement.settings).forEach(property => {
                                const propertyCSS = cssProperties[property] ?? {};

                                // if the property is not defined in the schema
                                // add the property to the schema with the defaults in "propertyCSS"
                                if (!(property in schema.properties.settings.properties)) {
                                    schema.properties.settings.properties[property] = applySchemaDefaults(property, {}, propertyCSS, true);
                                    
                                    // {
                                    //     title: propertyCSS.title ?? property,
                                    //     titlePosition: propertyCSS.titlePosition ?? "prefix",
                                    //     type: propertyCSS.type ?? "string",
                                    //     control: propertyCSS.control ?? undefined,
                                    //     width: propertyCSS.width ?? "100%",
                                    //     optional: true
                                    // };
                                }
                            });
                        }
                    }
                }
                else {
                    console.log("No schema found for settings for component (2): ", componentPackage, componentId);
                }
            }
            info = VEElement(selectedItem.pointer, { sx: { fontSize: "18px" } });
        }

        // update the schema
        setElementSchema(schema);

        return schema;
    }

    function applySchemaDefaults(propertyName, schema, propertyCSS, optional=true) {
        if (!schema) return;

        const defaults = {
            title: propertyName,
            type: "string",
            titlePosition: "prefix",
            width: "100%",
            optional: optional
        };
     
        return {
            ...defaults,
            ...schema,
            ...propertyCSS
        };
    }

    function prepareFormFields(schema = null) {
        if (!schema) return;

        let fields = {};

        // Go through the list of groups to build the form
        Object.keys(fieldGroups).forEach(group => {
            if (group === "componentSettings") {

                if (elementType === "component") {
                    // if this is a component, add settings schema from the component json

                    const settingsSchema = getFieldSchema(schema, "settings");
                    console.log("settingsSchema: ", settingsSchema);

                    if (settingsSchema && ("properties" in settingsSchema)) {

                        // for all items in the component settings schema
                        Object.keys(settingsSchema.properties).forEach(item => {
                            const itemSchema = getFieldSchema(settingsSchema, item);
                            if (!itemSchema) return;

                            // add the item as a fieldGroup in the form
                            fields[item] = {
                                id: item,
                                title: itemSchema.title ?? item,
                                description: itemSchema.description ?? undefined,
                                settingsSection: true,
                                clonable: settingsSchema.clonable ?? null,
                                maxClones: settingsSchema.maxClones ?? 0,
                                fields: {},
                            };

                            // fill the item fieldGroup with the fields in the schema
                            Object.keys(itemSchema.properties).forEach(field => {
                                const fieldSchema = getFieldSchema(itemSchema, field);
                                if (!fieldSchema) return;

                                if (field === "animate") {
                                    fields[item].fields[`settings.${item}.${field}`] = {
                                        properties: {}
                                    };

                                    if (typeof fieldSchema.properties === "object") {
                                        Object.keys(fieldSchema.properties).forEach(animateProperty => {
                                            fields[item].fields[`settings.${item}.${field}`].properties[`settings.${item}.${field}.${animateProperty}`] = fieldSchema.properties[animateProperty];
                                        });
                                    }
                                }
                                else {
                                    fields[item].fields[`settings.${item}.${field}`] = fieldSchema;
                                }
                            });
                        });
                    }
                }
                else if (elementType === "text") {
                    // if this is a text element, add settings schema from the text json

                    const settingsSchema = getFieldSchema(schema, "settings");
                    if (settingsSchema && ("properties" in settingsSchema)) {

                        fields["settings"] = {
                            title: "Settings",
                            settingsSection: true,
                            fields: {},
                        };

                        Object.keys(settingsSchema.properties).forEach(item => {
                            const fieldSchema = getFieldSchema(settingsSchema, item);
                            if (!fieldSchema) return;

                            fields["settings"].fields[`settings.${item}`] = fieldSchema;
                        });
                    }
                }
                else if (elementType === "subtitles") {
                    // if this is a subtitles element, add settings schema from the subtitles json

                    const settingsSchema = getFieldSchema(schema, "settings");
                    if (settingsSchema && ("properties" in settingsSchema)) {

                        fields["settings"] = {
                            title: "Settings",
                            fields: {}
                        };

                        Object.keys(settingsSchema.properties).forEach(item => {
                            let fieldSchema = getFieldSchema(settingsSchema, item);
                            if (!fieldSchema) return;

                            const fieldNameWithoutDashes = item.replace(/-/g, " ");
                            fieldSchema.title = fieldSchema.title ?? fieldNameWithoutDashes.charAt(0).toUpperCase() + fieldNameWithoutDashes.slice(1);

                            fields["settings"].fields[`settings.${item}`] = fieldSchema;
                        });
                    }
                }
            }
            else {
                // if not, add all fields from the group
                const fieldsInGroup = fieldGroups[group].fields ?? null;
                if (!fieldsInGroup) {
                    // console.log("Error: No fields in group: ", group); 
                    return;
                }

                let fieldsToAddToTheForm = {};

                Object.keys(fieldsInGroup).forEach(field => {
                    const fieldSchema = getFieldSchema(schema, field);
                    if (!fieldSchema) return; // if the field doesn't exist in the schema, skip it

                    const fieldTotalConfig = {
                        ...fieldSchema,
                        ...fieldsInGroup[field]
                    }

                    fieldsToAddToTheForm[field] = fieldTotalConfig;
                });

                if (Object.keys(fieldsToAddToTheForm).length > 0) {
                    fields[group] = {
                        title: fieldGroups[group].title,
                        description: fieldGroups[group].description,
                        fields: {},
                    };
    
                    fields[group].fields = fieldsToAddToTheForm;    
                }
            }
        });

        console.log("prepareFormFields(): fields: ", fields);
        setFormFields(fields);

        return fields;
    }

    function showForm() {
        return (
            <>
                {
                    Object.keys(formFields).map(groupId => {
                        const group = formFields[groupId];

                        return (
                            <Box key={groupId} className="flex flex-col p-2 pb-4 border-b border-gray-100">
                                {
                                    (group.title && group.title.length) &&
                                    <Box className="flex justify-between items-center p-1 text-base font-bold text-gray-600 cursor-default">
                                        <span key="title-icons" className="">
                                            {group.title}
                                            {addClonableGroupIcons(group)}
                                        </span>
                                        {
                                            (group.settingsSection) &&
                                            <VECSSPropertyAdder key="propertyAdder" {...props} groupId={groupId} animate={false} onPropertyAdd={onPropertyAdd} />
                                        }
                                    </Box>
                                }
                                <Box className="flex flex-wrap">
                                    {showFields(groupId, group)}
                                </Box>
                                
                            </Box>
                        );
                    })
                }
            </>
        );
    }

    function onPropertyAdd(groupId, property, animateField = false) {
        console.log("Add Property: ", groupId, property);
        console.log("elementSchema: ", elementSchema);

        if (elementSchema && ("properties" in elementSchema)) {
            if (("settings" in elementSchema.properties) && ("properties" in elementSchema.properties["settings"])) {
                let settingsProperties = elementSchema.properties["settings"].properties;

                if (groupId !== "settings") {
                    // if the element is a component, and the group is the settings item
                    settingsProperties[groupId] = settingsProperties[groupId] || {};
                    settingsProperties[groupId].properties = settingsProperties[groupId].properties || {};
                    settingsProperties = settingsProperties[groupId].properties;
                }

                if (animateField) {
                    // if the property to add is in the animate property
                    settingsProperties.animate = settingsProperties.animate || {};
                    settingsProperties.animate.properties = settingsProperties.animate.properties || {};
                    settingsProperties = settingsProperties.animate.properties;
                }

                // if the property doesn't exist, add it
                if (!(property in settingsProperties)) {
                    settingsProperties[property] = applySchemaDefaults(property, {}, cssProperties[property], true);
                    setElementSchema(elementSchema);
                    prepareFormFields(elementSchema);
                }
            }
        }
    }

    function onPropertyRemove(property) {
        // removes the property from the schema when the user clicks on the remove button
        console.log("Remove Property: ", property);
        console.log("elementSchema: ", elementSchema);

        const pathToTheProperty = property.split(".");
        const propertyToDelete = pathToTheProperty.pop();

        console.log("pathToTheProperty: ", pathToTheProperty, propertyToDelete);

        let targetSchema = elementSchema.properties;
        for (let i = 0; i < pathToTheProperty.length; i++) {
            const stepProperty = pathToTheProperty[i];

            if (!(stepProperty in targetSchema) || !("properties" in targetSchema[stepProperty])) {
                // if the path to the property doesn't exist, return
                console.log("Error: Path to the property doesn't exist: ", stepProperty, targetSchema);
                return;
            }

            targetSchema = targetSchema[stepProperty].properties;
        }

        if (propertyToDelete in targetSchema) {
            delete targetSchema[propertyToDelete];
            setElementSchema(elementSchema);
            prepareFormFields(elementSchema);
        }
    }

    function addClonableGroupIcons(group) {
        if (!group.clonable) return;

        return (
            <span className="ml-3">
                <ControlPointDuplicateIcon fontSize="small" className="text-gray-400 hover:text-gray-700 cursor-pointer" onClick={() => cloneItem(group)} />
                <DeleteForeverIcon fontSize="small" className="text-gray-400 hover:text-gray-700 cursor-pointer" onClick={() => deleteClonedItem(group)} />
            </span>
        );
    }

    function cloneItem(group) {

        try {
            if (!group.clonable) return;

            const clonableName = group.clonable;
            const clonableItemId = group.id;

            const itemSettings = movie.selected?.pointer.settings || null;
            if (!itemSettings) return;
            if (!(clonableItemId in itemSettings)) return;

            let itemToClone = JSON.parse(JSON.stringify(itemSettings[clonableItemId]));
            if (!itemToClone) return;

            let newNumber = 1;

            for (let i=1; i<=group.maxClones; i++) {
                const newItemId = clonableName + i.toString();
                if (newItemId in itemSettings) {
                    newNumber = i+1;
                }
            }

            if (newNumber > group.maxClones) {
                console.log("Max number of clones reached");
                return;
            }

            const newItemId = group.clonable + newNumber.toString();

            setMovie({
                type: "updateProperties",
                itemId: movie.selected.itemId,
                field: "settings",
                properties: {
                    [newItemId]: itemToClone
                }
            });

            setRefreshForm(refreshForm + 1);
        }
        catch (e) {
            console.log("Error cloning item: ", e);
        }
    }

    function deleteClonedItem(group) {
        try {
            if (!group.clonable) return;

            const clonableName = group.clonable;
            const clonableItemId = group.id;

            if (!movie.selected || !movie.selected.pointer || !movie.selected.pointer.settings) return;

            const itemSettings = JSON.parse(JSON.stringify(movie.selected.pointer.settings));
            if (!itemSettings) return;
            if (!(clonableItemId in itemSettings)) return;

            console.log("itemSettings: ", itemSettings);
            console.log("Deleting item: ", clonableItemId);

            itemSettings[clonableItemId] = undefined;
            // delete itemSettings[clonableItemId];
            
            // renumber the clonable items
            let newClonableItems = {}, newClonableIndex = 1;
            let newOtherItems = {};
            const keys = Object.keys(itemSettings);

            for (let i=0; i<keys.length; i++) {
                const key = keys[i];
                if (key.startsWith(clonableName)) {
                    if (itemSettings[key]) {
                        newClonableItems[clonableName + newClonableIndex.toString()] = itemSettings[key];
                        newClonableIndex++;
                    }
                }
                else {
                    newOtherItems[key] = itemSettings[key];
                }
            }

            // set the rest of clonable items to undefined
            for (let i=newClonableIndex; i<=group.maxClones; i++) {
                newClonableItems[clonableName + i.toString()] = undefined;
            }

            console.log("newClonableItems: ", newClonableItems);
            console.log("newOtherItems: ", newOtherItems);

            const newSettings = {
                ...newClonableItems,
                ...newOtherItems
            }

            console.log("newSettings: ", newSettings);

            setMovie({
                type: "updateProperties",
                itemId: movie.selected.itemId,
                field: "settings",
                properties: newSettings
            });

            setRefreshForm(refreshForm + 1);
        }
        catch (e) {
            console.log("Error deleting item: ", e);
        }
    }

    function showFields(groupId, group) {
        const response = [];

        const fields = group.fields, fieldList = Object.keys(fields);
        if (!fields) return response;

        for (let i = 0; i < fieldList.length; i++) {
            const fieldName = fieldList[i];

            // if fieldName ends with ".animate", skip it
            if (fieldName.endsWith(".animate")) continue;

            const field = fields[fieldName];

            // if there is no field.title, create it
            if (!field.title) {
                const titleWithoutDashes = fieldName.replace(/-/g, ' ');
                field.title = titleWithoutDashes.charAt(0).toUpperCase() + titleWithoutDashes.slice(1);
            }

            const fieldPath = fieldName.split(".");
            const property = fieldPath.pop();

            response.push(
                <VEField {...props}
                    key={fieldName} schema={field}
                    property={fieldName} value={getFieldValue(fieldName)}
                    onPropertyRemove={onPropertyRemove}
                />
            );
        }

        // if any of the fields in fieldList ends with ".animate", add the "Animate" section
        // Add the "Animate" section if any field in fieldList ends with ".animate"
        if (fieldList.some(fieldName => fieldName.endsWith(".animate"))) {
            response.push(
                <Box className="border border-gray-100 rounded-md p-2 mt-3 w-full hover:bg-gray-50">
                    <Box className="flex justify-between items-center p-1 cursor-default  text-gray-400 text-xs">
                        <span className="mr-3 font-bold">
                            Animate properties
                        </span>
                        <VECSSPropertyAdder {...props} groupId={groupId} animate={true} onPropertyAdd={onPropertyAdd} />
                    </Box>
                    <Box className="my-5 flex flex-wrap gap-2">
                        {showAnimateFields(fields)}
                    </Box>
                </Box>
            );
        }

        return response;

        function showAnimateFields(fields) {
            const response = [];
            const fieldList = Object.keys(fields);
            if (!fields) return response;

            for (let i = 0; i < fieldList.length; i++) {
                const fieldName = fieldList[i];

                // if it's not an "animate" property, skip it
                if (!fieldName.endsWith(".animate")) continue;

                const animateProperties = fields[fieldName].properties;
                if (!animateProperties) continue;

                const animatePropertiesList = Object.keys(animateProperties);

                for (let j = 0; j < animatePropertiesList.length; j++) {
                    const animateProperty = animatePropertiesList[j];
                    response.push(
                        <VEField {...props}
                            key={animateProperty} schema={animateProperties[animateProperty]}
                            property={animateProperty} value={getFieldValue(animateProperty)}
                            onPropertyRemove={onPropertyRemove}
                        />
                    );
                }
            }

            return response;
        }
    }

    function getFieldValue(fieldName) {
        const fieldPath = fieldName.split(".");
        let value = selectedElement;

        for (let i = 0; i < fieldPath.length; i++) {
            const field = fieldPath[i];
            if (field in value) value = value[field];
            else return "";
        }

        return value;
    }

    //
    // Helper functions
    //

    function getFieldSchema(schema, field) {

        // split the field by "." to get the path to the field
        const fieldPath = field.split(".");
        let fieldSchema = schema;

        // Go through the path while it exists
        // If it doesn't exist, return null
        for (let i = 0; i < fieldPath.length; i++) {
            const fieldName = fieldPath[i];
            if (("properties" in fieldSchema) && (fieldName in fieldSchema.properties)) {
                fieldSchema = fieldSchema.properties[fieldName];
            }
            else return null;
        }

        return fieldSchema;
    }



    //
    // Event handlers
    //

    function deleteItem() {
        if (movie.selected) {
            setMovie({
                type: "deleteItemByCoords",
                sceneIndex: movie.selected.sceneIndex,
                elementIndex: movie.selected.elementIndex
            });
        }

        // setElementSchema("hello " + elementType);
    }

    function duplicateItem() {
        if (movie.selected) {
            setMovie({
                type: "duplicateItemByCoords",
                sceneIndex: movie.selected.sceneIndex,
                elementIndex: movie.selected.elementIndex
            });
        }

        // console.log("duplicateItems(): formFields: ", formFields);
    }
}
